import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeftIcon, Loader2Icon } from "lucide-react";
import { useEffect, useReducer, useRef } from "react";
import bear1Image from "../bear1.png";
import bear2Image from "../bear2.png";
import selcaIcon from "../selca-icon-min.png";
import type { PhoneSize } from "./ui/phones/iphone";

type ProcessingConfig = {
  durationMs: number;
  icon?: React.ReactNode;
};

type Message = {
  id: string;
  text?: string;
  image?: string;
  sent: boolean;
  processing?: ProcessingConfig;
  timestamp?: string;
};

type IMessageScreenProps = {
  /** Contact name to display in header */
  contactName?: string;
  /** Array of messages to display */
  messages?: Message[];
  /** Size variant matching phone size */
  size?: PhoneSize;
};

const sizeStyles = {
  sm: {
    header: "gap-1 px-1 py-1",
    backIcon: "size-3",
    avatar: "size-6",
    avatarImg: "size-4",
    avatarImgSize: 16,
    contactName: "text-[9px]",
    messages: "gap-0.5 p-1.5",
    messageBubble: "px-1.5 py-0.5 text-[9px]",
    image: { width: 85, height: 85 },
    spinnerIcon: "size-2.5",
    inputBar: "p-1 px-2.5",
    plusButton: "size-4 text-xs",
    inputField: "px-1.5 py-0.5 text-[9px]",
  },
  md: {
    header: "gap-1.5 px-1.5 py-1.5",
    backIcon: "size-4",
    avatar: "size-8",
    avatarImg: "size-6",
    avatarImgSize: 24,
    contactName: "text-[11px]",
    messages: "gap-1 p-2.5",
    messageBubble: "px-2.5 py-1 text-xs",
    image: { width: 170, height: 170 },
    spinnerIcon: "size-3.5",
    inputBar: "p-1.5 px-3",
    plusButton: "size-6 text-base",
    inputField: "px-2.5 py-1 text-[11px]",
  },
  lg: {
    header: "gap-2 px-2.5 py-2.5",
    backIcon: "size-5",
    avatar: "size-10",
    avatarImg: "size-8",
    avatarImgSize: 32,
    contactName: "text-xs",
    messages: "gap-1 p-3",
    messageBubble: "px-3 py-1 text-sm",
    image: { width: 190, height: 190 },
    spinnerIcon: "size-4",
    inputBar: "p-2 px-4",
    plusButton: "size-7 text-lg",
    inputField: "px-3.5 py-1.5 text-xs",
  },
  xl: {
    header: "gap-2.5 px-3 py-3",
    backIcon: "size-6",
    avatar: "size-12",
    avatarImg: "size-10",
    avatarImgSize: 40,
    contactName: "text-sm",
    messages: "gap-2 p-4",
    messageBubble: "px-4 py-2 text-base",
    image: { width: 220, height: 220 },
    spinnerIcon: "size-5",
    inputBar: "p-2.5 px-5",
    plusButton: "size-8 text-xl",
    inputField: "px-4 py-2 text-sm",
  },
};

const defaultMessages: Message[] = [
  { id: "1", text: "Hey! Can you edit this image for me?", sent: true },
  { id: "2", image: bear1Image, sent: true },
  { id: "3", text: "nbp Please remove the bear's sunglasses.", sent: true },
  {
    id: "4",
    text: "🤖 Processing",
    processing: { durationMs: 2000 },
    sent: false,
  },
  { id: "5", image: bear2Image, sent: false },
  { id: "6", text: "dope pic, thx! 😊", sent: true },
];

type AnimationState = {
  visibleMessages: Message[];
  typingText: string;
  isTyping: boolean;
  currentIndex: number;
  completedProcessingIds: Set<string>;
};

type AnimationAction =
  | { type: "SHOW_MESSAGE"; message: Message }
  | { type: "START_TYPING" }
  | { type: "UPDATE_TYPING"; text: string }
  | { type: "STOP_TYPING" }
  | { type: "NEXT_MESSAGE" }
  | { type: "COMPLETE_PROCESSING"; messageId: string }
  | { type: "RESET" };

function animationReducer(
  state: AnimationState,
  action: AnimationAction
): AnimationState {
  switch (action.type) {
    case "SHOW_MESSAGE": {
      return {
        ...state,
        visibleMessages: [...state.visibleMessages, action.message],
      };
    }
    case "START_TYPING":
      return { ...state, isTyping: true, typingText: "" };
    case "UPDATE_TYPING":
      return { ...state, typingText: action.text };
    case "STOP_TYPING":
      return { ...state, isTyping: false, typingText: "" };
    case "NEXT_MESSAGE":
      return { ...state, currentIndex: state.currentIndex + 1 };
    case "COMPLETE_PROCESSING": {
      const newIds = new Set(state.completedProcessingIds);
      newIds.add(action.messageId);
      return { ...state, completedProcessingIds: newIds };
    }
    case "RESET":
      return {
        visibleMessages: [],
        typingText: "",
        isTyping: false,
        currentIndex: 0,
        completedProcessingIds: new Set(),
      };
    default:
      return state;
  }
}

/**
 * iMessage-style screen component for use inside phone mockups
 * Mimics the iOS Messages app interface with animated messages
 *
 * Effect is appropriate here: synchronizing with time (setTimeout/setInterval)
 * which is an external system. The animation should start when component displays.
 */
export function IMessageScreen({
  contactName = "Selca",
  messages = defaultMessages,
  size = "lg",
}: IMessageScreenProps) {
  const styles = sizeStyles[size];
  const [state, dispatch] = useReducer(animationReducer, {
    visibleMessages: [],
    typingText: "",
    isTyping: false,
    currentIndex: 0,
    completedProcessingIds: new Set<string>(),
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect is appropriate: synchronizing with time (external system)
  useEffect(() => {
    if (state.currentIndex >= messages.length) {
      // Reset and loop the animation
      timeoutRef.current = setTimeout(() => {
        dispatch({ type: "RESET" });
      }, 4000);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    const currentMessage = messages[state.currentIndex];
    if (!currentMessage) {
      return;
    }

    if (!currentMessage.sent) {
      // Received message: appear with slight delay
      timeoutRef.current = setTimeout(() => {
        dispatch({ type: "SHOW_MESSAGE", message: currentMessage });
        // If this is a processing message, wait for durationMs then mark complete and move on
        if (currentMessage.processing) {
          timeoutRef.current = setTimeout(() => {
            dispatch({
              type: "COMPLETE_PROCESSING",
              messageId: currentMessage.id,
            });
            dispatch({ type: "NEXT_MESSAGE" });
          }, currentMessage.processing.durationMs);
        } else {
          dispatch({ type: "NEXT_MESSAGE" });
        }
      }, 800);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    // Sent message: images appear instantly, text types out
    if (currentMessage.image) {
      timeoutRef.current = setTimeout(() => {
        dispatch({ type: "SHOW_MESSAGE", message: currentMessage });
        dispatch({ type: "NEXT_MESSAGE" });
      }, 500);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    // Sent text message: type it out in the input box
    dispatch({ type: "START_TYPING" });
    const text = currentMessage.text ?? "";
    let charIndex = 0;

    intervalRef.current = setInterval(() => {
      if (charIndex <= text.length) {
        dispatch({ type: "UPDATE_TYPING", text: text.slice(0, charIndex) });
        charIndex += 1;
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        // Wait a moment, then "send" the message
        timeoutRef.current = setTimeout(() => {
          dispatch({ type: "STOP_TYPING" });
          // Wait 250ms then show the message
          timeoutRef.current = setTimeout(() => {
            dispatch({ type: "SHOW_MESSAGE", message: currentMessage });
            dispatch({ type: "NEXT_MESSAGE" });
          }, 250);
        }, 200);
      }
    }, 50);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.currentIndex, messages]);

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-black">
      {/* Header */}
      <div
        className={`flex items-center ${styles.header} border-gray-200 border-b bg-gray-50/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80`}
      >
        <button className="flex items-center text-blue-500" type="button">
          <ChevronLeftIcon className={styles.backIcon} />
        </button>
        <div className="flex flex-1 flex-col items-center">
          <div
            className={`flex ${styles.avatar} items-center justify-center rounded-full bg-gradient-to-br from-rose-400 to-rose-500 dark:from-rose-300 dark:to-rose-400`}
          >
            <img
              alt={contactName}
              className={`${styles.avatarImg} object-contain`}
              height={styles.avatarImgSize}
              src={selcaIcon}
              width={styles.avatarImgSize}
            />
          </div>
          <span
            className={`mt-0.5 font-medium text-gray-900 ${styles.contactName} dark:text-white`}
          >
            {contactName}
          </span>
        </div>
        <div className={styles.backIcon} />
      </div>

      {/* Messages */}
      <div
        className={`flex flex-1 flex-col ${styles.messages} overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden`}
        style={{ scrollbarWidth: "none" }}
      >
        <AnimatePresence>
          {state.visibleMessages.map((message) => {
            const isProcessing =
              Boolean(message.processing) &&
              !state.completedProcessingIds.has(message.id);
            const spinnerIcon = message.processing?.icon ?? (
              <Loader2Icon className={styles.spinnerIcon} />
            );

            return (
              <motion.div
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className={`flex cursor-pointer ${message.sent ? "justify-end" : "justify-start"}`}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                key={message.id}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {message.image ? (
                  <motion.img
                    alt=""
                    className={`max-w-[75%] cursor-pointer rounded-2xl ${
                      message.sent ? "rounded-br-sm" : "rounded-bl-sm"
                    }`}
                    height={styles.image.height}
                    loading="lazy"
                    src={message.image}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    whileHover={{
                      scale: 1.03,
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                    }}
                    width={styles.image.width}
                  />
                ) : (
                  <motion.div
                    className={`flex max-w-[75%] cursor-pointer items-center gap-1.5 rounded-2xl ${styles.messageBubble} ${
                      message.sent
                        ? "rounded-br-sm bg-blue-500 text-white"
                        : "rounded-bl-sm bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-white"
                    }`}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                  >
                    {message.text}
                    {isProcessing ? (
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                      >
                        {spinnerIcon}
                      </motion.span>
                    ) : null}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Input bar */}
      <div
        className={`border-gray-200 border-t bg-gray-50/80 ${styles.inputBar} backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80`}
      >
        <div className="flex items-center gap-2">
          <div
            className={`flex ${styles.plusButton} items-center justify-center rounded-full bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400`}
          >
            <span className="leading-none">+</span>
          </div>
          <div
            className={`flex-1 rounded-full border border-gray-300 bg-white ${styles.inputField} dark:border-gray-600 dark:bg-gray-800`}
          >
            {state.isTyping ? (
              <motion.span
                animate={{ opacity: 1 }}
                className="text-gray-900 dark:text-white"
                initial={{ opacity: 0 }}
              >
                {state.typingText}
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  className="ml-0.5 inline-block h-3 w-0.5 bg-blue-500 align-middle"
                  transition={{
                    duration: 0.5,
                    repeat: Number.POSITIVE_INFINITY,
                  }}
                />
              </motion.span>
            ) : (
              <span className="text-gray-400">iMessage</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
